import { ReactNode, useState } from "react";

import { AnimateSize } from "@nens/chakra/components";
import { AsyncSubmitFunction, FormValuesExtend, RanaForm } from "@nens/ranalib";

import { EditCancelSubmitButtons } from "@/components/edit-cancel-submit-buttons";
import { HeaderBox } from "@/components/header-box";

export type HeaderAction<T extends FormValuesExtend> =
  | {
      type: "edit";
      canEdit: boolean;
      initialValues: T;
      onSubmit: AsyncSubmitFunction<T>;
      onCancel?: () => void;
      onEditStart?: () => void;
      isMutating: boolean;
    }
  | { type: "menu"; menuButton?: JSX.Element };

interface HeaderActionSectionProps<T extends FormValuesExtend> {
  title: string;
  action: HeaderAction<T>;
  children: (editing: boolean) => ReactNode;
}

export function HeaderActionSection<T extends FormValuesExtend>({
  title,
  action,
  children,
}: HeaderActionSectionProps<T>) {
  const [editing, setEditing] = useState(false);

  if (action.type === "menu") {
    return (
      <AnimateSize>
        <HeaderBox
          name={title}
          boxHeight={0}
          mb={0}
          borderBottomWidth={0}
          borderBottomRadius="none"
          button={action.menuButton}
        />
        {children(false)}
      </AnimateSize>
    );
  }

  const { canEdit, initialValues, onSubmit, onEditStart, onCancel, isMutating } = action;

  const submit: AsyncSubmitFunction<T> = async (values, setErrors) => {
    await onSubmit(values, setErrors);
    setEditing(false);
  };

  return (
    <AnimateSize>
      <RanaForm initialValues={initialValues} doSubmit={submit}>
        <HeaderBox
          name={title}
          boxHeight={0}
          mb={0}
          borderBottomWidth={0}
          borderBottomRadius="none"
          button={
            canEdit ? (
              <EditCancelSubmitButtons
                isEditing={editing}
                isMutating={isMutating}
                submitButtonProps={{ as: RanaForm.SubmitButton }}
                cancelButtonProps={{
                  onClick: () => {
                    onCancel?.();
                    setEditing(false);
                  },
                }}
                editButtonProps={{
                  onClick: () => {
                    onEditStart?.();
                    setEditing(true);
                  },
                }}
              />
            ) : undefined
          }
        />
        {children(editing)}
        <RanaForm.Errors />
      </RanaForm>
    </AnimateSize>
  );
}
